/* eslint-disable react-refresh/only-export-components -- the tone maps below are the chip's
   contract: a caller that renders a day status has to map the status to a tone AND to a label key,
   and splitting those two lines into a sibling module is how the two drift apart. Fast Refresh
   gives up on this file; it holds one presentational component and three pure lookups. */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { ReportStatusTone } from './types';

/**
 * The one status pill used across reports.
 *
 * Tones come from the design tokens (`success`/`warning`/`destructive`/`primary`), not from raw
 * Tailwind palette steps, for two reasons: `primary` is overridden per tenant at runtime, and the
 * `*-strong` text tokens exist precisely because the base token is a FILL colour that fails
 * contrast as text. Semantic colour is reserved for data — never for chrome.
 */

const TONE_CLASS: Record<ReportStatusTone, string> = {
  neutral: 'bg-muted text-muted-foreground-strong',
  positive: 'bg-success/10 text-success-strong',
  warning: 'bg-warning/10 text-warning-strong',
  critical: 'bg-destructive/10 text-destructive-strong',
  info: 'bg-primary/10 text-primary',
};

export interface StatusChipProps {
  tone: ReportStatusTone;
  children: React.ReactNode;
  /** Optional leading glyph. Decorative: the label carries the meaning. */
  icon?: LucideIcon;
  className?: string;
  'data-testid'?: string;
  title?: string;
}

export function StatusChip({
  tone,
  children,
  icon: Icon,
  className,
  title,
  'data-testid': testId,
}: StatusChipProps) {
  return (
    <span
      data-testid={testId}
      data-tone={tone}
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        TONE_CLASS[tone],
        className,
      )}
    >
      {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />}
      {children}
    </span>
  );
}

/* ── status vocabularies ──────────────────────────────────────────────────── */

/**
 * `AttendanceOvertimeDayView.DayStatus`, plus the two shapes the Shifts report adds. Absent is the
 * only critical tone: a late arrival is a question for the manager, an absence is a hole in the
 * month.
 *
 * `EarlyLeave` is the Shifts report's mirror of `Late` — the shift ended more than the grace before
 * the scheduled end. It is a warning for the same reason `Late` is: it is a question, not a verdict.
 * (`Unscheduled` was already here and means what it says on both reports: work with no schedule
 * behind it, which is also the CO-303 case whose overtime is an artifact.)
 */
const DAY_STATUS_TONE: Record<string, ReportStatusTone> = {
  Present: 'positive',
  Late: 'warning',
  EarlyLeave: 'warning',
  Absent: 'critical',
  Leave: 'info',
  HalfDayLeave: 'info',
  SickLeave: 'info',
  Holiday: 'neutral',
  WeekOff: 'neutral',
  Unscheduled: 'warning',
  Future: 'neutral',
};

/** `AttendanceOvertimeDayView.OvertimeApprovalStatus`. */
const OT_APPROVAL_TONE: Record<string, ReportStatusTone> = {
  NotRequired: 'neutral',
  Pending: 'warning',
  Approved: 'positive',
  Rejected: 'critical',
  Mixed: 'warning',
};

export function dayStatusTone(status: string | null | undefined): ReportStatusTone {
  return (status && DAY_STATUS_TONE[status]) || 'neutral';
}

export function otApprovalTone(status: string | null | undefined): ReportStatusTone {
  return (status && OT_APPROVAL_TONE[status]) || 'neutral';
}

/**
 * Label keys, not labels: the caller owns the `t()` call so i18n stays in the component tree.
 * An unknown status falls back to the raw server value rather than to a key that renders literally.
 */
export function dayStatusLabelKey(status: string | null | undefined): string {
  return status && DAY_STATUS_TONE[status] ? `reports.dayStatus.${status}` : '';
}

export function otApprovalLabelKey(status: string | null | undefined): string {
  return status && OT_APPROVAL_TONE[status] ? `reports.otStatus.${status}` : '';
}

export default StatusChip;
